import React, { useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { Button } from 'htlib-copy';
import { useAuth0 } from '@auth0/auth0-react'

import { usePostSingleMissionChecks } from 'hooks/checks';
import {
  useGetSingleMission,
  usePatchSingleMissionValidate,
  usePatchAdminSingleMissionValidate,
} from 'hooks/missions';

import AsyncUI from 'components/AsyncUI';
import InfoTable from 'components/InfoTable';

import { formatMissionData, useUrlParam } from './utils';

import MissingEntitiesModal from './MissingEntitiesModal';
import ValidationStatsModal from './ValidationStatsModal';
import Stats from './Stats';
import Checker from './Checker';

import './index.scss';

export default function SingleMission() {
  const { appendUrlParam, getUrlParam } = useUrlParam();
  const entityId = getUrlParam('entity');
  const tagValue = getUrlParam('tags');

  const { user } = useAuth0()
  const userRoles = (user && user['heuritech.roles']) || []
  const isAdmin = userRoles.includes('heuritech')

  const { missionId } = useParams();

  const refreshChecksMutation = usePostSingleMissionChecks(missionId);
  const validateMissionMutation = usePatchSingleMissionValidate();
  const adminValidateMissionMutation = usePatchAdminSingleMissionValidate();
  const getSingleMissionQuery = useGetSingleMission();

  const missionData = getSingleMissionQuery.data;

  const mission = missionData ? missionData.mission : {};
  const missionEntities = missionData ? missionData.missionEntities : [{}];
  const entitiesNotInThesaurus = missionData ? missionData.entitiesNotInThesaurus : null;

  const missionMeta = isAdmin
    ? formatMissionData(mission)
    : formatMissionData(mission).filter(({ label }) => label !== 'task _id');

  const currentEntity = entityId
    ? missionEntities.find(({ id }) => id === entityId)
    : missionEntities[0];
  const currentEntityId = currentEntity && currentEntity.id;

  const [stats, setStats] = useState({});
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showMissingEntitiesModal, setShowMissingEntitiesModal] = useState(true);

  useEffect(() => {
    if (adminValidateMissionMutation.data && adminValidateMissionMutation.data.updateStats) {
      setStats(adminValidateMissionMutation.data.updateStats);
    }
  }, [adminValidateMissionMutation.data]);

  useEffect(() => {
    if (!entityId && currentEntityId && tagValue !== 'no_for_all') {
      appendUrlParam('tags', 'yes');
      appendUrlParam('entity', currentEntityId);
    }
  }, [tagValue, currentEntityId, entityId, appendUrlParam]);

  return (
    <AsyncUI query={getSingleMissionQuery}>
      <div className="SingleMission">
        <div className="SingleMission__missionActions">
          <AsyncUI query={refreshChecksMutation}>
            <Button
              type="primary"
              ghost
              disabled={mission.check && mission.check.update_ongoing}
              onClick={() => {
                refreshChecksMutation.mutate();
              }}
            >
              Refresh checks
            </Button>
          </AsyncUI>

          {mission.check && mission.check.update_ongoing && (
            <p className="ongoingUpdate">checks refresh ongoing</p>
          )}

          <AsyncUI query={isAdmin ? adminValidateMissionMutation : validateMissionMutation}>
            {isAdmin ? (
              <Button
                type="success"
                onClick={() => {
                  if (
                    // eslint-disable-next-line no-alert
                    window.confirm(
                      'Checks will be propagated to labeling and mission will be marked as "checked". Do you want to continue ?',
                    ) === true
                  ) {
                    adminValidateMissionMutation.mutate(mission.id);
                    setShowStatsModal(true);
                  }
                }}
              >
                Check mission
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={() => {
                  if (
                    // eslint-disable-next-line no-alert
                    window.confirm(
                      'Mission will be marked as "validated". Do you want to continue ?',
                    ) === true
                  ) {
                    validateMissionMutation.mutate(mission.id);
                  }
                }}
              >
                Finish mission
              </Button>
            )}
          </AsyncUI>
        </div>

        <h3 className="sectionTitle">Mission meta</h3>
        <InfoTable data={missionMeta} />

        <Stats currentEntity={currentEntity} />

        {tagValue && <Checker missionEntities={missionEntities} />}
        {/* {entityId && tagValue && <Checker missionEntities={missionEntities} />} */}

        {entitiesNotInThesaurus && showMissingEntitiesModal && (
          <MissingEntitiesModal
            missingEntities={entitiesNotInThesaurus}
            onClose={setShowMissingEntitiesModal}
          />
        )}

        {showStatsModal && (
          <AsyncUI query={adminValidateMissionMutation}>
            <ValidationStatsModal
              stats={stats}
              onClose={() => {
                setShowStatsModal(false);
              }}
            />
          </AsyncUI>
        )}
      </div>
    </AsyncUI>
  );
}
